import { SyntheticEvent } from 'react';

import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  styled,
} from '@mui/material';

import { DialogProvider } from './DialogProvider';
import { tokens } from '../../theme/generated/tokens';
import { Sizes } from '../../types';

type DialogSize =
  | Sizes<'sm' | 'md' | 'lg' | 'xl'>
  | {
      width?: number | string;
      maxWidth?: number | string;
    };

/**
 * @deprecated Old dialog system — use `DialogV2` / `ConfirmationDialog` / `DestructiveDialog`
 * from `@cast/design-system` (`libs/design-system/src/components-v2/dialogs`).
 * To migrate a dialog that uses this, run the `/refactor-dialog <path>` skill.
 */
export type DialogProps = Omit<MuiDialogProps, 'onClose'> & {
  size?: DialogSize;
  disableCloseOnBackdropClick?: boolean;
  testId?: string;
  onClose?: (
    event?: SyntheticEvent,
    reason?: Parameters<Exclude<MuiDialogProps['onClose'], undefined>>[1]
  ) => void;
};

/**
 * @deprecated Old dialog system — use `slotProps={{ root: { size: N } }}` (numeric pixels) on
 * `DialogV2` / `ConfirmationDialog` / `DestructiveDialog` from `@cast/design-system` instead.
 * To migrate a dialog that uses this, run the `/refactor-dialog <path>` skill.
 */
export const DIALOG_PRESET_SIZES = {
  sm: { width: 440, maxWidth: 440 },
  md: { width: 560, maxWidth: 560 },
  lg: { width: 940, maxWidth: 940 },
  xl: { width: 1024, maxWidth: 1024 },
} as const;

const formatDimensionValue = (value?: number | string) => {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
};

const getDialogDimensions = (size: DialogSize) => {
  if (typeof size === 'object') {
    return {
      width: formatDimensionValue(size.width),
      maxWidth: formatDimensionValue(size.maxWidth),
    };
  }

  return DIALOG_PRESET_SIZES[size];
};

const StyledDialog = styled(MuiDialog)<{
  size: DialogSize;
}>(({ size }) => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'black',
    opacity: '0.85 !important',
  },
  '& .MuiDialog-paper': {
    padding: 0,
    backgroundColor: tokens.theme.bg.structural.surface,
    backgroundImage: 'none',
    boxShadow: tokens.theme.shadows.elevation.high,
    color: tokens.theme.fg.structural.primary,
    border: `1px solid ${tokens.theme.border.structural.neutral}`,
    ...getDialogDimensions(size),
  },
}));

/**
 * @deprecated Old dialog system — use `DialogV2` / `ConfirmationDialog` / `DestructiveDialog`
 * from `@cast/design-system` (`libs/design-system/src/components-v2/dialogs`).
 * To migrate a dialog that uses this, run the `/refactor-dialog <path>` skill.
 */
const Dialog = ({
  size = 'md',
  children,
  disableCloseOnBackdropClick,
  testId,
  ...rest
}: DialogProps) => {
  return (
    <StyledDialog
      size={size}
      {...rest}
      onClose={(event, reason) => {
        if (disableCloseOnBackdropClick && reason === 'backdropClick') {
          return;
        }
        rest.onClose?.(event as SyntheticEvent, reason);
      }}
      data-testid={testId}
    >
      <DialogProvider destroy={rest.onClose}>{children}</DialogProvider>
    </StyledDialog>
  );
};

export default Dialog;
